import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import globalUrls from "../globalUrls";
import ProtectedRoute from "../components/common/ProtectedRoute";
import globalRoles from "../globalRoles";
import { Spin } from "antd";
import { useFeatureFlags } from "../application/TenantsSlice/useFeatureFlags";

const UnauthorizedPage = lazy(() => import("../pages/UnauthorizedPage"));
const LandingPage = lazy(() => import("../pages/LandingPage"));
const AdminLoginPage = lazy(() => import("../pages/AdminLoginPage"));
const AdminHomePage = lazy(() => import("../pages/AdminHomePage"));
const OnlineStorePage = lazy(() => import("../pages/OnlineStorePage"));
const SelectDistritoPage = lazy(() => import("../pages/SelectDistritoPage"));
const ClientLoginPage = lazy(() => import("../pages/ClientLoginPage"));
const ClientRegisterPage = lazy(() => import("../pages/ClientRegisterPage"));
const CheckoutPage = lazy(() => import("../pages/CheckoutPage"));
const CheckoutAnonPage = lazy(() => import("../pages/CheckoutAnonPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const ClientEventsPage = lazy(() => import("../pages/ClientEventsPage"));
const ClientForgotPassPage = lazy(
  () => import("../pages/ClientForgotPassPage")
);
const ClientChangePassPage = lazy(
  () => import("../pages/ClientChangePassPage")
);
const BlogPage = lazy(() => import("../pages/BlogPage"));
const BlogPostPage = lazy(() => import("../pages/BlogPostPage"));
const ClientHomePage = lazy(() => import("../pages/ClientHomePage"));
const AboutUsPage = lazy(
  () => import("../tenantMatrix/AboutUsPage")
);
const PrivacyPolicyPage = lazy(() => import("../pages/PrivacyPolicyPage"));
const ClientHomeAddressesPage = lazy(
  () => import("../pages/ClientHomeAddressesPage")
);
const ClientHomeOrdersPage = lazy(
  () => import("../pages/ClientHomeOrdersPage")
);
const ClientHomeProfilePage = lazy(
  () => import("../pages/ClientHomeProfilePage")
);
const OrderTransportationPage = lazy(
  () => import("../pages/OrderTransportationPage")
);
const OrderPackerStandalonePage = lazy(
  () => import("../pages/OrderPackerStandalonePage")
);
const ErrorPage = lazy(() => import("../pages/ErrorPage"));
const MisCitasPage = lazy(() => import("../pages/CalendarSlice/MisCitasPage"));
const ServiciosPublicPage = lazy(() => import("../pages/CalendarSlice/ServiciosPublicPage"));
const CalendarioPublicPage = lazy(() => import("../pages/CalendarSlice/CalendarioPublicPage"));

const AppRoutes: React.FC = () => {
  const { isUrlEnabled } = useFeatureFlags();
  const location = useLocation();

  return (
    <Suspense
      fallback={
        <div className="page-loading">
          <Spin size="small" />
        </div>
      }
    >
      <div className="page-transition-wrapper" key={location.pathname}>
        <div className="page-transition">
          <Routes>
            <Route path={globalUrls.LANDING_PAGE} element={<LandingPage />} />

            <Route
              path={globalUrls.SPECIAL_EVENTS}
              element={<ClientEventsPage />}
            />

            {/* Public store entry point: actual store */}
            <Route
              path={globalUrls.ONLINE_STORE}
              element={<OnlineStorePage />}
            />

            {/* Selection page (clients and anon) */}
            {isUrlEnabled(globalUrls.ONLINE_STORE_SELECT_DISTRITO) && (
              <Route
                path={globalUrls.ONLINE_STORE_SELECT_DISTRITO}
                element={<SelectDistritoPage />}
              />
            )}
            {!isUrlEnabled(globalUrls.ONLINE_STORE_SELECT_DISTRITO) && (
              <Route
                path={globalUrls.ONLINE_STORE_SELECT_DISTRITO}
                element={<OnlineStorePage />}
              />
            )}

            <Route
              path={globalUrls.ONLINE_STORE_CHECKOUT_ANON}
              element={<CheckoutAnonPage />}
            />

            {isUrlEnabled(globalUrls.SPECIAL_EVENTS) && (
              <Route
                path={globalUrls.SPECIAL_EVENTS}
                element={<ClientEventsPage />}
              />
            )}

            {isUrlEnabled(globalUrls.BLOG) && (
              <Route path={globalUrls.BLOG} element={<BlogPage />} />
            )}

            {isUrlEnabled(globalUrls.BLOG_POST) && (
              <Route path={globalUrls.BLOG_POST} element={<BlogPostPage />} />
            )}

            <Route path={globalUrls.ABOUT_US} element={<AboutUsPage />} />

            <Route
              path={globalUrls.PRIVACY_POLICY}
              element={<PrivacyPolicyPage />}
            />

            <Route
              path={globalUrls.CLIENT_LOGIN}
              element={<ClientLoginPage />}
            />

            <Route
              path={globalUrls.CLIENT_REGISTER}
              element={<ClientRegisterPage />}
            />

            <Route
              path={globalUrls.CLIENT_FORGOT_PASSWORD}
              element={<ClientForgotPassPage />}
            />

            <Route
              path={globalUrls.CLIENT_CHANGE_PASSWORD}
              element={<ClientChangePassPage />}
            />

            {/* ✅ Protected client pages */}
            <Route
              path={globalUrls.CLIENT_HOME}
              element={
                <ProtectedRoute roles={[globalRoles.CLIENT]}>
                  <ClientHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={globalUrls.CLIENT_HOME_PROFILE}
              element={
                <ProtectedRoute roles={[globalRoles.CLIENT]}>
                  <ClientHomeProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={globalUrls.CLIENT_HOME_ADDRESSES}
              element={
                <ProtectedRoute roles={[globalRoles.CLIENT]}>
                  <ClientHomeAddressesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={globalUrls.CLIENT_HOME_ORDERS}
              element={
                <ProtectedRoute roles={[globalRoles.CLIENT]}>
                  <ClientHomeOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={globalUrls.ONLINE_STORE_CHECKOUT}
              element={
                <ProtectedRoute roles={[globalRoles.CLIENT]}>
                  <CheckoutPage />
                </ProtectedRoute>
              }
            />

            {/* ✅ Public services listing — anon + clients */}
            {isUrlEnabled(globalUrls.SERVICES_PUBLIC) && (
              <Route
                path={globalUrls.SERVICES_PUBLIC}
                element={<ServiciosPublicPage />}
              />
            )}

            {/* ✅ Public group session calendar */}
            {isUrlEnabled(globalUrls.PUBLIC_CALENDAR) && (
              <Route
                path={globalUrls.PUBLIC_CALENDAR}
                element={<CalendarioPublicPage />}
              />
            )}

            {/* ✅ My appointments dashboard — Client only */}
            {isUrlEnabled(globalUrls.APPOINTMENTS) && (
              <Route
                path={globalUrls.ACCOUNT_APPOINTMENTS}
                element={
                  <ProtectedRoute roles={[globalRoles.CLIENT]}>
                    <MisCitasPage />
                  </ProtectedRoute>
                }
              />
            )}

            {/* 🔐 Admin-only */}
            <Route path={globalUrls.ADMIN_LOGIN} element={<AdminLoginPage />} />

            <Route
              path={globalUrls.ADMIN_HOME}
              element={
                <ProtectedRoute roles={[globalRoles.ADMIN]}>
                  <AdminHomePage />
                </ProtectedRoute>
              }
            />

            <Route
              path={globalUrls.ORDER_TRANSPORTATION}
              element={
                <ProtectedRoute
                  roles={[globalRoles.ADMIN, globalRoles.TRANSPORTER]}
                >
                  <OrderTransportationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path={globalUrls.PACKER}
              element={
                <ProtectedRoute roles={[globalRoles.ADMIN, globalRoles.PACKER]}>
                  <OrderPackerStandalonePage />
                </ProtectedRoute>
              }
            />

            <Route
              path={globalUrls.UNAUTHORIZED}
              element={<UnauthorizedPage />}
            />
            <Route
              path={globalUrls.ERROR}
              element={<ErrorPage />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </div>
    </Suspense>
  );
};

export default AppRoutes;
